import React, { useEffect, useState } from "react";
import { Row, Col, Container, Form } from "react-bootstrap";
import PropTypes from 'prop-types';
import { object } from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import { Card, CardContent } from '@mui/material';
import BackNext from '../../Common/SubComponents/BackNext';
import { SESSION_STORAGE_ACCOUNT_KEY, SESSION_STORAGE_SSO_ACCESS_TOKEN_KEY, getSessionStorageItem, deleteConsumerApplicationId } from '../../../../sessionStorage';
import ExceedCovgAmountModal from "../Helpers/ExceedCovgAmountModal";
import PrivacyPolicy from '../../../Disclosure/PrivacyPolicy';
import TermsOfUse from '../../../Disclosure/TermsOfUse';
import PolicyPurposeConfirmation from "./PolicyPurposeConfirmation";
import AgreementToUnderwriting from "./AgreementToUnderwriting";
import { AgreementCardContent } from "../Helpers/AgreementToUnderwritingData";

export default function Confirmation(props) {

    const [isLoading, setIsLoading] = useState(false);
    const [showFailureMessage, setShowFailureMessage] = useState(false);
    const [showValidationMessage, setShowValidationMessage] = useState(false);
    const [exceedCovgAmtModalShow, setExceedCovgAmtModalShow] = useState(false);

    const formMethods = useForm({
        resolver: yupResolver(Confirmation.ValidationSchema(props.fullFormState.steps.basicInfo.birthdate)),
        mode: 'all',
        reValidateMode: 'all',
        defaultValues: { ...props.fullFormState.steps.policyPurposeConfirmation, ...props.fullFormState.steps.agreementToUnderwriting },
    });

    const { register, handleSubmit, setValue, trigger, reset } = formMethods;

    const state = props.fullFormState.steps.basicInfo && props.fullFormState.steps.basicInfo.state;

    const onSubmitError = () => {
        const body = {
            'consumerAccountId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).account.accountId,
            'oneClickAccountId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).account.oneClickAccountId,
            'dob': props.fullFormState.steps.basicInfo.birthdate,
            'firstname': props.fullFormState.steps.personalInfo.firstName,
            'lastname': props.fullFormState.steps.personalInfo.lastName,
            'gender': props.fullFormState.steps.basicInfo.gender,
            'healthclass': props.fullFormState.steps.quoter.healthClass,
            'state': props.fullFormState.steps.gettingToKnowYou.mailingState,
            'zipCode': props.fullFormState.steps.gettingToKnowYou.mailingZip,
            'phone': props.fullFormState.steps.identityVerification.secondaryAddresseePhoneNumber,
            'face': props.fullFormState.steps.quoter.coverageAmount,
            'tobacco': props.fullFormState.steps.basicInfo.tobacco === 'true',
            'email': props.fullFormState.steps.basicInfo.email || props.fullFormState.steps.personalInfo.emailAddress,
            'term': parseInt(props.fullFormState.steps.quoter.term, 10),
            'quotedpremium': props.fullFormState.steps.quoter.premium,
            'quotedpremiummode': 'M',
            'specificappointmentutc': null,
            'specialNote': 'Concierge case created due to Americo Loan Protector Underwriting API Failure, please reach out to consumer for assistance.',
        };

        const accessToken = getSessionStorageItem(SESSION_STORAGE_SSO_ACCESS_TOKEN_KEY);
        const covrServicesUrl = window.__ENV__.REACT_APP_COVR_SERVICES_URL;
    
        fetch(`${covrServicesUrl}/api/Case/PostApp`, {
            method: 'POST',
            headers: {
            'Content-Type': 'application/json',
            'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId,
            'X-Consumer-Application-ID': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).consumerApplicationId,
            'Authorization': accessToken ? 'Bearer ' + accessToken : null,
            },
            body: JSON.stringify(body),
        })
        .then(deleteConsumerApplicationId)
        .then((data) => {
            if (data.success) {
                setShowFailureMessage(true);
            }
            setIsLoading(false);
        }).catch(() => {
            setIsLoading(false);
        });
    };

    const onSubmit = () => {
        const policyPurposeConfirmation = props.fullFormState.steps.policyPurposeConfirmation;
        const agreementToUnderwriting = props.fullFormState.steps.agreementToUnderwriting;
        const isIneligible = policyPurposeConfirmation.pendingApplications === 'true' || agreementToUnderwriting.agreedToUnderwritingTerms === 'false';
        if (isIneligible) {
            props.onDeclined();
        }
        else {
            if (props.fullFormState.steps.quoter.coverageAmount > 1000000) { setExceedCovgAmtModalShow(true); }
            else {
                let totalDeathBenefit = 0;
                props.fullFormState.steps.policyPurposeConfirmation.policies.forEach((policy) => {
                    totalDeathBenefit += parseInt(policy.faceAmount, 10);
                });

                if (props.fullFormState.steps.policyPurposeConfirmation.existingCoverage === "true" && props.fullFormState.steps.policyPurposeConfirmation.replaceExistingCoverage === "true" && totalDeathBenefit > props.fullFormState.steps.policyPurposeConfirmation.totalInsuredAmountExternal) {
                    setShowValidationMessage(true);
                }
                else {
                    setShowValidationMessage(false);
                    setShowFailureMessage(false);
                    setIsLoading(true);
                    // fix tobacco to bool even though we asked for timeline...
                    props.fullFormState.steps.basicInfo.tobacco = props.fullFormState.steps.basicInfo.tobacco !== 'Never';
                    const covrServicesUrl = window.__ENV__.REACT_APP_COVR_SERVICES_URL;
                    fetch(`${covrServicesUrl}/api/Americo/Submit`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId,
                            'X-Consumer-Application-ID': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).consumerApplicationId,
                        },
                        body: JSON.stringify(props.fullFormState),
                    }).then(res => res.json())
                        .then(data => {
                            if (data.policyNumber !== "" && data.policyNumber !== "error") {
                                props.setPolicyNumber(data.policyNumber);
                            } else {
                                props.onDeclined();
                            }
                        }).catch(() => {
                            onSubmitError();
                        });
                }
            }
        }
    };

    //go next only after policynumber has been set to form state to ensure it's logged
    useEffect(() => {
        if (props.fullFormState.policyNumber !== "" && props.fullFormState.policyNumber !== "error") {
            props.onNext();
        }
    }, [props.fullFormState.policyNumber]);

    useEffect(() => {
        if (props.fullFormState.steps.policyPurposeConfirmation.existingCoverage === "false") {
            props.onFieldChange('replaceExistingCoverage', "false");
        }
    }, [props.fullFormState.steps.policyPurposeConfirmation.existingCoverage]);

    const DisclosureLink = {
        privacyPolicy: 'PrivacyPolicy',
        termsOfUse: 'TermsOfUse',
    };

    const [disclosureDialogOpen, setDisclosureDialogOpen] = useState(false);

    const handleDisclosureLink = (link) => {
        if (link === DisclosureLink.privacyPolicy) {
            setComponent(<PrivacyPolicy />);
        } else if (link === DisclosureLink.termsOfUse) {
            setComponent(<TermsOfUse />);
        }
        setDisclosureDialogOpen(true);
    };

    const [component, setComponent] = useState(null);

    return (
        <Container>
            <div className="formPage">
                <FormProvider {...formMethods}>
                    <Form className="quote-form" onSubmit={handleSubmit(onSubmit)}>
                        <PolicyPurposeConfirmation
                            formState={props.fullFormState.steps.policyPurposeConfirmation}
                            fullFormState={props.fullFormState}
                            onFieldChange={(field, value) => props.onFieldChange('policyPurposeConfirmation', field, value)}
                            showValidationMessage={showValidationMessage}
                            register={register}
                            setValue={setValue}
                            trigger={trigger}
                            reset={reset}
                        />
                        <Row className="justify-content-center">
                            <Col lg={7}>
                                <Card className="agreementCard mb-3">
                                    <CardContent className="scrollbar">
                                        <AgreementCardContent
                                            state={state}
                                            handleDisclosureLink={handleDisclosureLink}
                                            DisclosureLink={DisclosureLink}
                                            isReplaceExistingCoverage={props.fullFormState.steps.policyPurposeConfirmation.replaceExistingCoverage}
                                        />
                                    </CardContent>
                                </Card>
                            </Col>
                        </Row>
                        <AgreementToUnderwriting
                            formState={props.fullFormState.steps.agreementToUnderwriting}
                            onFieldChange={(field, value) => props.onFieldChange('agreementToUnderwriting', field, value)}
                            fullFormState={props.fullFormState}
                            basicInfo={props.fullFormState.steps.basicInfo}
                            disclosureDialogOpen={disclosureDialogOpen}
                            setDisclosureDialogOpen={setDisclosureDialogOpen}
                            component={component}
                            register={register}
                            setValue={setValue}
                        />
                        {showFailureMessage && (
                            <Row className="justify-content-center">
                                <Col lg={7}>
                                    <div className="text-danger text-center">We’re sorry, something has gone wrong, but don’t worry - your request is registered and one of our licensed concierge team members will reach out to you.</div>
                                </Col>
                            </Row>
                        )}
                        <BackNext onBack={props.onBack} disabled={showFailureMessage} loading={isLoading} colSize={2} width={100} continueLabel={{ text: 'Next' }} />
                    </Form>
                </FormProvider>
            </div>
            {exceedCovgAmtModalShow &&
                <ExceedCovgAmountModal
                    fullFormState={props.fullFormState}
                    onHide={() => setExceedCovgAmtModalShow(false)}
                    show={() => setExceedCovgAmtModalShow(true)}
                    onMultiFieldChange={props.onMultiFieldChange}
                />
            }
        </Container>
    );
}

Confirmation.propTypes = {
    onNext: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired,
    onFieldChange: PropTypes.func.isRequired,
    fullFormState: PropTypes.object,
    onDeclined: PropTypes.func,
    onMultiFieldChange: PropTypes.func,

};

Confirmation.ValidationSchema = (birthdate) => {
    return object().shape({ ...PolicyPurposeConfirmation.ValidationSchema(birthdate).fields, ...AgreementToUnderwriting.ValidationSchema.fields });
};
